import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThreadsBatchParams, useThreadsBatch } from '@screentone/addon-auth-wrapper';
import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  Group,
  IconExternalLink,
  IconTakeOver,
  Loader,
  Tooltip,
  Typography
} from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { ConfirmationDialog } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { getPageLockThreadId, PageLockProvider } from 'contexts/page-lock/PageLockContext';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import { AvailableFeatureFlags, MultiListContainer, NewswiresListContainer } from 'data/generated/graphql';
import { useSelectedCollections } from 'features/multicollection-edit/contexts/SelectedCollectionsContext';
import { useConvertedProperty } from 'hooks';
import { useIsAdmin, useIsDebug } from 'hooks/roles';
import styles from './Sidebar.module.scss';

interface CheckboxRowProps {
  newswiresList: NewswiresListContainer;
}

const CheckboxRow = ({ newswiresList }: CheckboxRowProps) => {
  const currentProperty = useConvertedProperty();
  const isAdmin = useIsAdmin();
  const isDebug = useIsDebug();
  const queryClient = useQueryClient();
  const { selectedCheckboxes, setSelectedCheckboxes, syncedCollectionsRef } = useSelectedCollections();
  const [showButtons, setShowButtons] = useState(false);
  const { state, lockPageFragment, releasePageFragment, loading: collectionLockLoading } = usePageLock();
  const rowState = state.publishing;
  const { alertWarning, alertError } = useAlert();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Automatically tick the checkbox if the collection is locked by the current user on mount
  useEffect(() => {
    if (!collectionLockLoading) {
      setSelectedCheckboxes((prev) => ({ ...prev, [newswiresList.uuid]: rowState.isLockedByMe }));
    }
  }, [collectionLockLoading, newswiresList.uuid, rowState.isLockedByMe, setSelectedCheckboxes]);

  useEffect(() => {
    // If the collection is locked by another user, untick the checkbox and show an alert on take over
    if (!collectionLockLoading && rowState.hasTakenOver && rowState.lockedBy) {
      alertWarning(
        `${rowState.lockedBy.name} has taken over the publishing rights of the ${newswiresList.attributes.name} collection.`
      );

      setSelectedCheckboxes((prev) => ({ ...prev, [newswiresList.uuid]: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    collectionLockLoading,
    newswiresList.attributes.name,
    rowState.hasTakenOver,
    rowState.lockedBy,
    setSelectedCheckboxes,
    newswiresList.uuid
  ]);

  const resetCollectionQuery = useCallback(() => {
    // Clear the sync guard so NewswiresList re-syncs fresh data after the query resets.
    // Without this, staleTime: Infinity + the syncedCollectionsRef guard would suppress re-sync
    // even though the query cache is cleared.
    delete syncedCollectionsRef.current[newswiresList.uuid];
    return queryClient.resetQueries({
      queryKey: [
        'CollectionDTOById',
        {
          id: newswiresList.attributes.allessehId,
          publicationKey: newswiresList.attributes.publicationKey
        }
      ],
      exact: true
    });
  }, [
    queryClient,
    syncedCollectionsRef,
    newswiresList.uuid,
    newswiresList.attributes.allessehId,
    newswiresList.attributes.publicationKey
  ]);

  const handleChange = useCallback(
    async ({ target }: { target: HTMLInputElement }) => {
      setIsLoading(true);

      try {
        if (target.checked) {
          await lockPageFragment('publishing');
          // Refetch fresh collection data so changes published from other tabs are reflected.
          // resetQueries (not invalidateQueries) is required because React Query's structural sharing
          // would otherwise return the same data reference on an unchanged response, and
          // NewswiresList's sync guard would suppress re-sync — leaving the panel empty.
          await resetCollectionQuery();
        } else {
          await releasePageFragment('publishing');
        }
        setSelectedCheckboxes((prev) => ({ ...prev, [newswiresList.uuid]: target.checked }));
      } catch (error) {
        console.error('Error changing page lock state:', error);
        alertError('An error occurred while changing the collection state. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSelectedCheckboxes, lockPageFragment, releasePageFragment, newswiresList, resetCollectionQuery]
  );

  const handleTakeOver = useCallback(async () => {
    setIsLoading(true);

    try {
      await lockPageFragment('publishing', true);
      // Refetch fresh collection data so changes published from other tabs are reflected.
      await resetCollectionQuery();
    } catch (error) {
      console.error('Error taking over page lock:', error);
      alertError('An error occurred while taking over the collection. Please try again.');
    } finally {
      setIsLoading(false);
      setShowModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockPageFragment, resetCollectionQuery]);

  const isErroredCollection = newswiresList.attributes.createdUtc === 0;
  const label = isErroredCollection ? (
    <Typography color="lava">{newswiresList.attributes.allessehId}</Typography>
  ) : (
    newswiresList.attributes.name
  );

  const isLockedByOther = useMemo(
    () => rowState.isLocked && rowState.isReadOnly,
    [rowState.isLocked, rowState.isReadOnly]
  );

  const isChecked = useMemo(
    () => (isLockedByOther || selectedCheckboxes[newswiresList.uuid]) ?? false,
    [isLockedByOther, selectedCheckboxes, newswiresList.uuid]
  );

  const shouldShowTakeOver = useMemo(() => showButtons && isLockedByOther, [showButtons, isLockedByOther]);
  const isAdminOrDebug = useMemo(() => isAdmin || isDebug, [isAdmin, isDebug]);
  const shouldShowOpenSingleCollectionView = useMemo(
    // For admin or debug users, always show the button.
    // For the rest of the users, only show if the collection is not taken over by another user.
    () => showButtons && (isAdminOrDebug || !shouldShowTakeOver),
    [showButtons, isAdminOrDebug, shouldShowTakeOver]
  );

  const takeOverDescription = useMemo(
    () =>
      `${rowState.lockedBy?.name ?? 'Unknown user'} is currently curating ${
        newswiresList.attributes.name
      }. Are you sure you want to take control? Their unsaved changes will be lost.`,
    [rowState.lockedBy?.name, newswiresList.attributes.name]
  );

  return (
    <Group
      direction="row"
      gap="xs"
      fullWidth
      onMouseEnter={() => setShowButtons(true)}
      onMouseLeave={() => setShowButtons(false)}
    >
      <FormLabel margin={{ bottom: 'sm' }} label={label} labelPosition="right" wrap className={styles.formLabel}>
        {!isLoading && !collectionLockLoading ? (
          <Checkbox
            name={`checkbox-${newswiresList.uuid}`}
            checked={isChecked}
            onChange={handleChange}
            className={styles.checkbox}
            disabled={isLockedByOther}
          />
        ) : (
          <Loader size="md" />
        )}
      </FormLabel>
      {shouldShowTakeOver && (
        <Tooltip delay="none" usePortal className={styles.tooltip}>
          <Tooltip.Content id={`tooltipLockInfo-${newswiresList.uuid}`}>
            Held by: {rowState.lockedBy?.name ?? 'Unknown user'}
          </Tooltip.Content>
          <Tooltip.Trigger aria-describedby={`tooltipLockInfo-${newswiresList.uuid}`}>
            <Button tertiary icon={IconTakeOver} onClick={() => setShowModal(true)} />
          </Tooltip.Trigger>
        </Tooltip>
      )}
      {shouldShowOpenSingleCollectionView && (
        <Tooltip delay="none" usePortal className={styles.tooltip}>
          <Tooltip.Content id={`tooltipOpenSingleCollection-${newswiresList.uuid}`}>
            Open single collection view
          </Tooltip.Content>
          <Tooltip.Trigger aria-describedby={`tooltipOpenSingleCollection-${newswiresList.uuid}`}>
            <Button
              tertiary
              icon={IconExternalLink}
              componentEl={Link}
              to={`/${currentProperty}/collections/${newswiresList.attributes.allessehId}`}
              target="_blank"
            />
          </Tooltip.Trigger>
        </Tooltip>
      )}
      {showModal && (
        <ConfirmationDialog
          description={takeOverDescription}
          isLoading={isLoading}
          onClickFunc={handleTakeOver}
          showDialog={setShowModal}
          title="Collection Claim"
          confirmLabel="Claim Collection"
        />
      )}
    </Group>
  );
};

export const Sidebar = () => {
  const { root } = useDataModelContext<MultiListContainer>();

  const threadIds = useMemo(
    () => root.collection.map((c) => getPageLockThreadId(c.attributes.allessehId)),
    [root.collection]
  );

  const batchParams: ThreadsBatchParams = useMemo(() => ({ threadIds, limit: 1, sort: 'DESC' }), [threadIds]);

  const { threadsData = {}, loading } = useThreadsBatch(batchParams);

  const CheckboxList = useMemo(
    () =>
      root.collection.map((newswiresList) => (
        <PageLockProvider
          key={newswiresList.uuid}
          allessehId={newswiresList.attributes.allessehId}
          fragments={['publishing']}
          featureFlag={AvailableFeatureFlags.CollectionLockingEnabled}
          skipInitialFetch
          initialMessages={threadsData[getPageLockThreadId(newswiresList.attributes.allessehId)]}
        >
          <CheckboxRow newswiresList={newswiresList} />
        </PageLockProvider>
      )),
    [root.collection, threadsData]
  );

  return (
    <Box className={styles.sidebar} margin={{ top: 'sm' }}>
      <Box.Title>Collections</Box.Title>
      <Box.Content padding={{ vertical: 'md', left: 'md' }}>
        {loading ? <Loader size="md" /> : CheckboxList}
      </Box.Content>
    </Box>
  );
};
